/**
 * Common type definitions for element composition related interfaces
 */

import { Element } from './Element';

/**
 * Type for element categories to ensure consistent usage
 */
export type ElementCategory = 'macronutrient' | 'secondary' | 'micronutrient' | 'beneficial' | 'other';

/**
 * Element categories constants
 */
export const ELEMENT_CATEGORIES = {
  MACRONUTRIENT: 'macronutrient' as ElementCategory,
  SECONDARY: 'secondary' as ElementCategory,
  MICRONUTRIENT: 'micronutrient' as ElementCategory,
  BENEFICIAL: 'beneficial' as ElementCategory,
  OTHER: 'other' as ElementCategory
};

/**
 * Interface for element composition with percentage
 */
export interface ElementComposition {
  symbol: string;
  percentage: number;
  atomicWeight?: number;
  name?: string;
  color?: string;
  category?: ElementCategory;
}

/**
 * Interface for salt form data
 */
export interface SaltFormData {
  name: string;
  formula: string;
  density: number | null;
  solubility: number | null;
  isCustom: boolean;
  elements: ElementComposition[];
}

/**
 * Interface for Salt Composition Analysis component props
 */
export interface SaltCompositionAnalysisProps {
  saltName: string;
  formula?: string;
  elements: ElementComposition[];
  density?: number | null;
  solubility?: number | null;
  molecularWeight?: number | null;
}

// Element color mapping
export const ELEMENT_COLORS: Record<string, string> = {
  N: '#3498db',   // Nitrogen - blue
  P: '#f39c12',   // Phosphorus - orange
  K: '#9b59b6',   // Potassium - purple
  Ca: '#e74c3c',  // Calcium - red
  Mg: '#2ecc71',  // Magnesium - green
  S: '#f1c40f',   // Sulfur - yellow
  Fe: '#e67e22',  // Iron - rust
  Mn: '#95a5a6',  // Manganese - gray
  Zn: '#1abc9c',  // Zinc - teal
  Cu: '#34495e',  // Copper - dark blue
  B: '#7f8c8d',   // Boron - dark gray
  Mo: '#d35400',  // Molybdenum - dark orange
  Cl: '#16a085',  // Chlorine - green blue
  Na: '#27ae60',  // Sodium - light green
  Si: '#8e44ad',  // Silicon - dark purple
  Co: '#c0392b',  // Cobalt - dark red
  // Default color for other elements
  default: '#bdc3c7' // light gray
};
